import {PayrollPayFrequency} from "../../../__generated__/generated_types";
import {getLastDayOfMonth} from "../../../utils/months-helper";

/**
 * Note: this logic is translated from the Python code in the calc_server project.
 *
 * Generally the daysPerPeriod is redundant and can be removed from the code below as not used. I just kept it
 * for now to match the Python code.
 *
 * @see https://github.com/vizibly-inc/vizibly/blob/main/vizibly-calc-server/calc_server/payroll/payroll_dates.py
 */

export type PayPeriodsPerMonth = {
    reforecast: number[],
    budget: number[],
}

export function addDays(date: Date, days: number): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));
}

/**
 * This function calculates the starting date for payroll calculations given the frequency, reforecast date, and
 * payroll seed date. This only applicable and useful for Weekly & BiWeekly.
 *
 * @param frequency - How often payroll is processed (aka paid to the employee).
 * @param reforecastYear - The reforecast year for starting payroll calculations.
 * @param seedDate - Used calculate starting pay date for payroll. This value must be before the reforecast year.
 */
function payrollInitialDate(
    frequency: PayrollPayFrequency,
    reforecastYear: number,
    seedDate: Date
): Date {
    if (frequency !== PayrollPayFrequency.Weekly && frequency !== PayrollPayFrequency.BiWeekly) {
        throw new Error(`Cannot calculate initial date for PayFrequency: ${frequency}.`);
    }

    // Weekly and BiWeekly need a seed_date to start from. Without the seed_date, we can't figure
    // out when the first pay date is during the reforecast year.
    //
    // Basics
    //   1. Determine the difference in the years.
    //      a. If greater than 1, we need to bring the initial date up to year before the reforecast year.
    //         We must do this by weeks as, adding by years will change the day of the week, but this needs
    //         to stay the same.
    //   2. Since adding by weeks will necessarily put us out ~month, we use a loop to bring us to-date with
    //      reforecast year.
    //
    //   FYI - you cannot just do a single math statement to get it right date, the while loop at the end is
    //         required.

    const deltaWeeks = frequency === PayrollPayFrequency.Weekly ? 1 : 2;
    const deltaMs = deltaWeeks * 7 * 24 * 60 * 60 * 1000;

    const reforecastStart = new Date(Date.UTC(reforecastYear, 0, 1));
    const reforecastPreviousEnd = new Date(Date.UTC(reforecastYear - 1, 11, 31));
    let initialDate = new Date(Date.UTC(seedDate.getUTCFullYear(), seedDate.getUTCMonth(), seedDate.getUTCDate()));
    const yearDiff = reforecastYear - initialDate.getUTCFullYear();

    if (yearDiff > 1) {
        initialDate = new Date(initialDate.getTime() + (52 * (yearDiff - 1)) * 7 * 24 * 60 * 60 * 1000);
    } else if (yearDiff < 0) {
        initialDate = new Date(initialDate.getTime() - (52 * (-yearDiff)) * 7 * 24 * 60 * 60 * 1000);
    }

    if (initialDate < reforecastStart) {
        while (initialDate < reforecastStart) {
            initialDate = new Date(initialDate.getTime() + deltaMs);
        }
    } else if (initialDate > reforecastStart) {
        while (initialDate > reforecastPreviousEnd) {
            initialDate = new Date(initialDate.getTime() - deltaMs);
        }
        initialDate = new Date(initialDate.getTime() + deltaMs);
    }

    return initialDate;
}

/**
 * Calculates the list of pay dates for a given PayFrequency and Reforecast Year. A seed date is used
 * for the Weekly and BiWeekly PayFrequencies and should be the year before the reforecast year.
 *
 * @param frequency - The frequency at which employees are paid.
 * @param reforecastYear - The reforecast year of the property. It is this year and the next year for which
 *                         dates are generated.
 * @param seedDate - Used for Weekly and BiWeekly to calculate appropriate pay dates.
 * @returns A tuple containing the list of pay dates and the list of days per period.
 */
export function generatePayDates(
    frequency: PayrollPayFrequency,
    reforecastYear: number,
    seedDate?: Date | null
): [Date[], number[]] {
    if (frequency === PayrollPayFrequency.Monthly) {
        const ret: Date[] = [];
        const daysPerPeriod: number[] = [];
        let idx = 0;
        for (const y of [reforecastYear, reforecastYear + 1]) {
            for (let m = 1; m <= 12; m++) {
                const lastDayInMonth = getLastDayOfMonth(m - 1, y);
                // Use UTC so downstream UTC-based logic (custom dates, summaries)
                // sees the correct calendar day regardless of local timezone.
                ret[idx] = new Date(Date.UTC(y, m - 1, lastDayInMonth));
                daysPerPeriod[idx] = lastDayInMonth;
                idx++;
            }
        }
        return [ret, daysPerPeriod];
    }

    if (frequency === PayrollPayFrequency.SemiMonthly) {
        const ret: Date[] = [];
        const daysPerPeriod: number[] = [];
        let idx = 0;
        for (const y of [reforecastYear, reforecastYear + 1]) {
            for (let m = 1; m <= 12; m++) {
                // 15th of month in UTC
                ret[idx] = new Date(Date.UTC(y, m - 1, 15));
                daysPerPeriod[idx] = 15;

                const lastDayInMonth = getLastDayOfMonth(m - 1, y);
                // Last day of month in UTC
                ret[idx + 1] = new Date(Date.UTC(y, m - 1, lastDayInMonth));
                daysPerPeriod[idx + 1] = lastDayInMonth - 15;
                idx += 2;
            }
        }
        return [ret, daysPerPeriod];
    }

    if (!seedDate) {
        throw new Error("seedDate is required for Weekly and BiWeekly frequencies");
    }

    const startDate = payrollInitialDate(frequency, reforecastYear, seedDate);
    let size: number;
    let deltaMs: number;
    let daysPerPeriod: number[];

    if (frequency === PayrollPayFrequency.BiWeekly) {
        // (26 * 2): 2 years reforecast & budget
        size = 52;
        deltaMs = 14 * 24 * 60 * 60 * 1000; // 2 weeks in milliseconds
        daysPerPeriod = Array(size).fill(14);
    } else if (frequency === PayrollPayFrequency.Weekly) {
        // (52 * 2): 2 years reforecast & budget
        size = 104;
        deltaMs = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds
        daysPerPeriod = Array(size).fill(7);
    } else {
        throw new Error(`Unknown PayFrequency: ${frequency}`);
    }

    const ret: Date[] = [];
    let currentDate = new Date(startDate);
    for (let idx = 0; idx < size; idx++) {
        ret[idx] = new Date(currentDate);
        currentDate = new Date(currentDate.getTime() + deltaMs);
    }
    return [ret, daysPerPeriod];
}

export function getPayPeriodsPerMonth(reforecastYear: number, payDates: Date[]): PayPeriodsPerMonth {
    const ret: PayPeriodsPerMonth = {
        reforecast: new Array(12).fill(0),
        budget: new Array(12).fill(0)
    };

    for(const payDate of payDates) {
        if(reforecastYear == payDate.getUTCFullYear()) {
            ret.reforecast[payDate.getUTCMonth()]! += 1;
        }
        else if(reforecastYear + 1 == payDate.getUTCFullYear()) {
            ret.budget[payDate.getUTCMonth()]! += 1;
        }
    }

    return ret;
}